import { useState } from 'react'

import { Box, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import {
  DeleteOneOnOneMeetingDocument,
  OneOnOneMeetingsDocument,
  OneOnOnePageHeaderDocument,
  OneOnOneParticipantFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NewMeetingModal from 'modules/one_on_ones/components/NewMeetingModal'
import PersonCard from 'modules/one_on_ones/components/PersonCard'
import { timeAgoWhenRecent } from 'modules/one_on_ones/lib/date'

const MeetingsListPage = () => {
  const { employee } = useCurrentSession()
  const [showNewModal, setNewAddModal] = useState(false)
  const { oneOnOneId } = useStrictParams<'oneOnOneId'>()
  const { navigation } = useNavigation()
  const { openDialog } = useConfirmationDialog()
  const {
    data: oneOnOneData,
    loading: oneOnOneLoading,
    error: oneOnOneError,
  } = useQuery(OneOnOnePageHeaderDocument, {
    variables: {
      id: String(oneOnOneId),
    },
  })
  const [deleteMeeting, { loading: deletingMeeting }] = useMutation(DeleteOneOnOneMeetingDocument, {
    extractorFn: (result) => result.oneOnOnesMutations.deleteOneononesMeeting,
  })

  const onDeleteMeeting = async (meetingId: string) => {
    const response = await deleteMeeting({
      variables: {
        id: meetingId,
      },
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            id: meetingId,
            __typename: 'OneOnOnesMeeting',
          }),
        })
      },
    })

    const handled = handleResult(response, () => null)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('one_on_ones.meeting.delete_modal.success'),
      })
    }

    return handled
  }

  const oneOnOne = oneOnOneData?.oneOnOnes.oneOnOne
  const isParticipant =
    oneOnOne?.firstParticipant?.id === employee?.id ||
    oneOnOne?.secondParticipant?.id === employee?.id
  const participant = [oneOnOne?.firstParticipant, oneOnOne?.secondParticipant].find(
    (p) => p?.id === employee?.id
  )
  let otherParticipants: (OneOnOneParticipantFragment | undefined)[] = [
    oneOnOne?.firstParticipant,
    oneOnOne?.secondParticipant,
  ].filter((p): p is NonNullable<typeof p> => p != null && p.id !== employee?.id)

  if (otherParticipants.length === 1) {
    otherParticipants = [...otherParticipants, undefined]
  }

  const source = useGraphqlDataCollectionSource({
    document: OneOnOneMeetingsDocument,
    mapData: (data) => data.oneOnOnes.meetingsConnection.nodes,
    mapPagination: (data) => ({
      total: Number(data.oneOnOnes.meetingsConnection.totalCount),
    }),
    search: {
      enabled: true,
    },
    sortings: {
      startTime: {
        label: i18n.t('one_on_ones.meetings.date'),
      },
    },
    defaultSortings: {
      field: 'startTime',
      order: 'desc',
    },
    mapVariables: ({ search }) => {
      return {
        oneOnOneId: String(oneOnOneId),
        search,
      }
    },
    itemUrl(item) {
      return navigation.performanceOneOnOnes.oneOnOne.meetings.meeting.fullPath({
        oneOnOneId: String(oneOnOneId),
        meetingId: item.id,
      })
    },
    itemActions(item) {
      return compact([
        item._policies?.delete.can
          ? {
              label: i18n.t('common.delete'),
              icon: Delete,
              enabled: !deletingMeeting,
              onClick: () =>
                openDialog({
                  title: i18n.t('one_on_ones.meeting.delete_modal.title'),
                  illustration: 'delete',
                  content: i18n.t('one_on_ones.meeting.delete_modal.description'),
                  cancelLabel: i18n.t('common.cancel'),
                  confirmLabel: i18n.t('one_on_ones.meeting.delete_modal.delete'),
                  onConfirm: () => onDeleteMeeting(item.id),
                }),
            }
          : undefined,
      ])
    },
    primaryActions: () => {
      if (!isParticipant) {
        return undefined
      }
      return {
        label: i18n.t('one_on_ones.new.action'),
        icon: Add,
        onClick: () => setNewAddModal(true),
      }
    },
  })

  return (
    <ResponsivePage.Body showHeader={false} loading={oneOnOneLoading} error={oneOnOneError}>
      {Boolean(otherParticipants.length) && (
        <Box width="full" flexDirection="column">
          <Box width="full" flexDirection="row">
            {Array(2)
              .fill(null)
              .map((_, index) => {
                const participant = otherParticipants[index]

                if (participant) {
                  return (
                    <Box
                      key={participant.id}
                      width="full"
                      paddingRight={index === 0 ? 's8' : undefined}
                    >
                      <PersonCard participant={participant} />
                    </Box>
                  )
                }

                return <Box key={`empty-${index}`} width="full" />
              })}
          </Box>
          <Box height="s24" />
        </Box>
      )}

      {Boolean(participant) && showNewModal && (
        <NewMeetingModal
          employeeId={String(otherParticipants[0]?.id)}
          onClose={() => setNewAddModal(false)}
          refetchQueries={[OneOnOneMeetingsDocument]}
        />
      )}

      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('one_on_ones.meetings.date'),
                  render: (record) => timeAgoWhenRecent(record.startTime),
                },
                {
                  label: i18n.t('one_on_ones.meetings.total_tasks'),
                  render: (record) => record.tasks.length,
                },
              ],
            },
          },
        ]}
      />
    </ResponsivePage.Body>
  )
}

export default MeetingsListPage
